import { computed, ref, watch, type Ref } from 'vue';
import type { EntityDocument } from '../types/entities';

type EntityVersionPanelStore = {
  loadEntityVersions(): Promise<void>;
};

type EntityVersionPanelOptions = {
  store: EntityVersionPanelStore;
  activeEntity: Readonly<Ref<EntityDocument | null>>;
  isOpen: Readonly<Ref<boolean>>;
  isWorkspaceReadonly: Readonly<Ref<boolean>>;
};

export function useEntityVersionPanel({ store, activeEntity, isOpen, isWorkspaceReadonly }: EntityVersionPanelOptions) {
  const displayedEntityVersionId = ref('');
  const displayedEntityRevisionId = ref<string | null>(null);
  const versionHistoryError = ref('');

  watch([activeEntity, isOpen], async ([entity, open], _previous, onCleanup) => {
    let stale = false;
    onCleanup(() => { stale = true; });
    versionHistoryError.value = '';
    if (!entity || !open) return;
    try { await store.loadEntityVersions(); }
    catch (error) {
      if (!stale) versionHistoryError.value = error instanceof Error ? error.message : 'Versionen konnten nicht geladen werden.';
    }
  });

  const displayedEntityVersion = computed(() => activeEntity.value?.versions.find((version) => version.id === displayedEntityVersionId.value)
    ?? activeEntity.value?.versions.find((version) => version.status === 'active')
    ?? null);
  const displayedEntityRevision = computed(() => displayedEntityVersion.value?.revisions.find((revision) => revision.id === displayedEntityRevisionId.value) ?? null);
  const isHistoricalVersion = computed(() => Boolean(activeEntity.value
    && (displayedEntityRevisionId.value || displayedEntityVersionId.value !== activeEntity.value.workingVersion.id)));
  const isVersionReadonly = computed(() => isWorkspaceReadonly.value || isHistoricalVersion.value);
  const displayedVersionLabel = computed(() => displayedEntityRevision.value
    ? `${displayedEntityVersion.value?.name} · Revision ${displayedEntityRevision.value.revision}`
    : displayedEntityVersion.value?.name ?? 'Version');

  return {
    displayedEntityVersionId,
    displayedEntityRevisionId,
    displayedEntityVersion,
    displayedEntityRevision,
    isHistoricalVersion,
    isVersionReadonly,
    displayedVersionLabel,
    versionHistoryError
  };
}
